import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/pageHeader";
import Button from "../../components/button";
import ButtonWrapper from "../../components/button/wrapper";
import config from "../../config";
import BigNumber from "bignumber.js";
import useWalletHelpers from "../../hooks/useWalletHelpers";
import { digit, networkType } from "../../constants";
import {
  formatBigNumber,
  formatNumberWithMinMaxDigits,
} from "../../utils/filter";
import { useTokenHelpers } from "../../hooks/useTokenHelpers";
import useDirectExchangeHelpers from "../../hooks/useDirectExchangeHelpers";
import { useMainConstant } from "../../contexts/mainContext";
import { useEthereum } from "../../contexts/etherruemContext";
import { useNotification } from "../../contexts/notificationContext";
import { toWei } from "../../utils/token";
import ConfirmDirectExchangeModal from "../../components/swap/confirmExchangModal";
import { useUnifiedWallet } from "../../providers/UnifiedWalletProvider";
import { DirectExchange as IDirectExchange } from "../../interfaces/config";

export interface DirectExchangeDetail {
  configExchange: IDirectExchange;
  poolBalances: BigNumber;
  myOriginTokenBalance: BigNumber;
  myDestinationTokenBalance: BigNumber;
  exchangeRate: BigNumber;
  feeRate: BigNumber;
}

export interface Props {
  index: number;
}

const toPrecision = (value: string, digit: number = 8) => {
  return BigNumber(value).toFixed(digit, 1).toString();
};

const DirectExchangeUsdtUsdv: React.FC<Props> = ({ index = 2 }) => {
  const [isPending, setIsPending] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [displayBalanceTokenA, setDisplayBalanceTokenA] = useState("0.00");
  const [displayBalanceTokenB, setDisplayBalanceTokenB] = useState("0.00");
  const [balanceTokenA, setBalanceTokenA] = useState("0");

  const { addTokenOnWallet } = useWalletHelpers();
  const { checkAllowanceToSpender } = useTokenHelpers();
  const { getAllExchangeInfo } = useDirectExchangeHelpers();
  const { approveTokenToSpender } = useTokenHelpers();

  const mainConstant = useMainConstant();
  const ethereum = useEthereum();
  const { setNotify } = useNotification();
  const { exchange } = useDirectExchangeHelpers();
  const { walletApi } = useUnifiedWallet();

  useEffect(() => {
    initialDirectExchangeDetail();
    const intervalId = setInterval(() => {
      fetchData();
    }, 5000);
    fetchData();

    return () => clearInterval(intervalId);
  }, [
    walletApi.account.current,
    ethereum?.contracts,
    ethereum?.contracts?.directExchange,
  ]);

  const initialDirectExchangeDetail = () => {
    return {
      configExchange: config.directExchange[index],
      poolBalances: BigNumber("0"),
      myOriginTokenBalance: BigNumber("0"),
      myDestinationTokenBalance: BigNumber("0"),
      exchangeRate: BigNumber("0"),
      feeRate: BigNumber("0"),
      pause: false,
    };
  };

  const [directExchangeDetail, setDirectExchangeDetail] =
    useState<DirectExchangeDetail>(initialDirectExchangeDetail());

  const fetchData = async () => {
    checkStatus(); // Re-add checkStatus call
    const result: any = await getAllExchangeInfo();
    setDirectExchangeDetail((prev) => {
      return { ...prev, ...result[index] };
    });
  };

  const checkStatus = useCallback(async () => {
    const account = walletApi.account.current;
    if (!account) return;
    try {
      const result = await checkAllowanceToSpender(
        directExchangeDetail.configExchange.originToken.code ?? "",
        account,
        directExchangeDetail.configExchange.contractAddress ?? ""
      );
      console.log("checkStatus result:", result);
      setIsApproved(result);
    } catch (err) {
      console.error("checkStatus error:", err);
    }
  }, [
    checkAllowanceToSpender,
    directExchangeDetail.configExchange.originToken.code,
    directExchangeDetail.configExchange.contractAddress,
    walletApi.account,
  ]);

  const tokenBalanceOf = useCallback(
    async (tokenSymbol: string) => {
      try {
        if (tokenSymbol === "BNB_TOKEN" && walletApi.account.current) {
          return await ethereum.web3?.eth.getBalance(walletApi.account.current);
        }

        // Add null checks to prevent "Cannot read properties of null" error
        if (!ethereum.contracts || !ethereum.contracts.tokens) {
          console.warn("Ethereum contracts not initialized yet");
          return null;
        }

        console.log(
          "ethereum.contracts.tokens",
          tokenSymbol,
          ethereum.contracts.tokens
        );

        // Additional check for the specific token contract
        if (!ethereum.contracts.tokens[`${tokenSymbol}`]) {
          console.warn(`Token contract not found for: ${tokenSymbol}`);
          return null;
        }

        return await ethereum.contracts.tokens[`${tokenSymbol}`].methods
          .balanceOf(walletApi.account.current)
          .call();
      } catch (error) {
        console.error("tokenBalanceOf", tokenSymbol, error);
        return null;
      }
    },
    [ethereum.web3, ethereum.contracts, walletApi.account]
  );

  const getBalances = useCallback(async () => {
    try {
      console.log(
        "getBalances",
        directExchangeDetail.configExchange.originToken
      );
      if (
        directExchangeDetail.configExchange.originToken &&
        directExchangeDetail.configExchange.originToken?.symbol &&
        directExchangeDetail.configExchange.originToken?.symbol !== "" &&
        ethereum?.web3
      ) {
        console.log(
          "getBalances",
          directExchangeDetail.configExchange.originToken
        );
        const balance = await tokenBalanceOf(
          directExchangeDetail.configExchange.originToken.code
        );
        console.log("getBalances", balance);
        if (balance) {
          console.log(
            "checkAllowanceToSpenderFromMax x:",
            ethereum?.web3.utils.fromWei(balance, "ether")
          );
          setBalanceTokenA(ethereum?.web3.utils.fromWei(balance, "ether"));
          setDisplayBalanceTokenA(
            toPrecision(
              ethereum?.web3.utils.fromWei(balance, "ether").toLocaleString(),
              4
            )
          );
        }
      }

      console.log(
        "getBalances",
        directExchangeDetail.configExchange.destinationToken
      );
      if (
        directExchangeDetail.configExchange.destinationToken &&
        directExchangeDetail.configExchange.destinationToken?.symbol &&
        directExchangeDetail.configExchange.destinationToken?.symbol !== "" &&
        ethereum?.web3
      ) {
        console.log(
          "getBalances",
          directExchangeDetail.configExchange.originToken
        );
        const balance = await tokenBalanceOf(
          directExchangeDetail.configExchange.destinationToken.code
        );

        console.log("getBalances", balance);
        if (balance) {
          setDisplayBalanceTokenB(
            toPrecision(
              ethereum?.web3.utils.fromWei(balance, "ether").toLocaleString(),
              4
            )
          );
        }
      }
    } catch (error) {
      console.error("getBalances", error);
    }
  }, [
    directExchangeDetail.configExchange.originToken,
    directExchangeDetail.configExchange.destinationToken,
    ethereum?.web3,
    tokenBalanceOf,
  ]);

  // Load balances when account changes
  useEffect(() => {
    if (
      walletApi.account.current &&
      ethereum.contracts &&
      ethereum.contracts.tokens
    ) {
      getBalances();
    } else {
      setDisplayBalanceTokenA("0.00");
      setDisplayBalanceTokenB("0.00");
    }
  }, [walletApi.account, getBalances, ethereum.contracts]);

  const addToken = () => {
    const { address, image, name } =
      directExchangeDetail.configExchange.destinationToken;

    addTokenOnWallet(
      address ?? "",
      name,
      image ?? "",
      digit.TOKEN_DIGIT,
      networkType.ERC20,
      {
        onReceipt: () => console.log("added!"),
        onError: (error) => console.log(error),
      }
    );
  };

  const destinationPerOrigin = useMemo(() => {
    return parseFloat(directExchangeDetail.exchangeRate.toString()) > 0
      ? BigNumber(1).div(directExchangeDetail.exchangeRate)
      : BigNumber("0");
  }, [directExchangeDetail?.exchangeRate]);

  const unlockWallet = () => {
    mainConstant.setIsShowConnectModal(true);
  };

  const isInSufficientPoolBalances = useMemo(() => {
    return directExchangeDetail.poolBalances.isZero();
  }, [directExchangeDetail?.poolBalances]);

  const onUpdateNotification = () => {
    return {
      onTransactionHash: (txHash: any) => {
        setNotify({
          status: "processing",
          txHash,
        });
      },
      onError: (e: any, receipt?: any) => {
        setIsPending(false);
        setNotify({
          status: "failed",
          txHash: receipt ? receipt.transactionHash : null,
          errorReason: e?.shortMessage || "Transaction failed",
        });
      },
      onReceipt: ({
        transactionHash,
        status,
      }: {
        transactionHash: any;
        status: boolean;
      }) => {
        setNotify({
          status: status ? "success" : "failed",
          txHash: transactionHash,
        });
      },
    };
  };

  const onExchange = async (inputAmount: string) => {
    try {
      setIsPending(true);
      const account = walletApi.account.current ?? "";
      await exchange(
        directExchangeDetail.configExchange.contractAddress ?? "",
        account,
        toWei(inputAmount),
        onUpdateNotification()
      );
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setIsPending(false);
    }
  };

  const approve = async () => {
    setIsPending(true);
    const account = walletApi.account.current ?? "";
    try {
      console.log("approve 1");
      await approveTokenToSpender(
        directExchangeDetail.configExchange.originToken.address ?? "",
        account,
        directExchangeDetail.configExchange.contractAddress ?? "",
        onUpdateNotification()
      );
      await checkStatus();
      console.log("approve 2");
      // The approval notification callback will handle status checking
    } catch (err) {
      console.error(err);
      setIsPending(false); // Make sure to reset pending state on error
    }
  };

  // Debug effect to track approval state changes
  useEffect(() => {
    console.log("isApproved state changed:", isApproved);
  }, [isApproved]);

  // Debug effect to track button state
  useEffect(() => {
    console.log("Button state debug:", {
      isConnected: walletApi.isConnected,
      isInSufficientPoolBalances,
      isPending,
      isApproved,
      originTokenCode: directExchangeDetail.configExchange.originToken?.code,
      contractAddress: directExchangeDetail.configExchange.contractAddress,
    });
  }, [
    walletApi.isConnected,
    isInSufficientPoolBalances,
    isPending,
    isApproved,
    directExchangeDetail.configExchange,
  ]);

  // Check approval status when contract details are available
  useEffect(() => {
    if (
      walletApi.account.current &&
      directExchangeDetail.configExchange.originToken?.code &&
      directExchangeDetail.configExchange.contractAddress
    ) {
      console.log("Checking approval status...");
      checkStatus();
    }
  }, [
    walletApi.account,
    directExchangeDetail.configExchange.originToken?.code,
    directExchangeDetail.configExchange.contractAddress,
    checkStatus,
  ]);

  return (
    <div className="md:container p-0">
      <div className="xl:ml-32 xl:max-w-[976px]">
        <PageHeader
          title="Direct Exchange USDV-USDT"
          subTitle="Convert your USDV to USDT directly with the fixed platform exchange rate"
        ></PageHeader>

        <div className="mx-4 mb-6 md:mx-0 p-4 bg-[#909090] bg-opacity-5 rounded-[2px] border border-[#19506F]">
          <div className="grid grid-rows-1 xl:grid-cols-2 gap-4">
            <div className="flex items-center">
              <img
                className="mr-2 h-8 w-8"
                src={directExchangeDetail.configExchange.originToken.image}
                alt={directExchangeDetail.configExchange.originToken.name}
              />
              <div>
                <div className="text-sm mb-1">
                  {directExchangeDetail.configExchange.originToken.name}
                </div>
                <div>
                  My balance:{" "}
                  <span className="text-number">
                    {formatNumberWithMinMaxDigits(
                      Number(displayBalanceTokenA),
                      4,
                      8
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className="hidden xl:flex items-center ml-auto">
              <div className="flex flex-col">
                <div className="text-sm text-velo-label">
                  {directExchangeDetail.configExchange.destinationToken.name}{" "}
                  available in pool
                </div>
                <div className="text-right mt-1 text-number">
                  {formatBigNumber(directExchangeDetail.poolBalances, 2)}
                </div>
              </div>
            </div>
            <div className="xl:hidden rounded-[2px] p-[10px] bg-[#909090] bg-opacity-5">
              <div className="text-sm text-velo-label">
                {directExchangeDetail.configExchange.destinationToken.name}{" "}
                available in pool
              </div>
              <div className="text-right text-number">
                {formatBigNumber(directExchangeDetail.poolBalances, 2)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4 xl:mt-6">
            <div className="rounded-sm p-2.5 bg-gray-500 bg-opacity-5">
              <div className="text-velo-label text-sm flex items-center">
                <img
                  className="mr-1 h-4 w-4"
                  src={directExchangeDetail.configExchange.originToken.image}
                  alt={directExchangeDetail.configExchange.originToken.name}
                />
                My {directExchangeDetail.configExchange.originToken.name}{" "}
                balance
              </div>
              <div className="text-right mt-1 text-number">
                {formatNumberWithMinMaxDigits(
                  Number(displayBalanceTokenA),
                  4,
                  8
                )}
              </div>
            </div>

            <div className="rounded-sm p-2.5 bg-gray-500 bg-opacity-5">
              <div className="text-velo-label text-sm flex items-center">
                <img
                  className="mr-1 h-4 w-4"
                  src={
                    directExchangeDetail.configExchange.destinationToken.image
                  }
                  alt={
                    directExchangeDetail.configExchange.destinationToken.name
                  }
                />
                My {directExchangeDetail.configExchange.destinationToken.name}{" "}
                balance
              </div>
              <div className="text-right mt-1 text-number">
                {formatNumberWithMinMaxDigits(
                  Number(displayBalanceTokenB),
                  4,
                  8
                )}
              </div>
            </div>

            <div className="rounded-sm p-2.5 bg-gray-500 bg-opacity-5">
              <div className="text-velo-label text-sm flex items-center">
                <img
                  className="mr-1 h-4"
                  src={directExchangeDetail.configExchange.pairTokenImage}
                  alt="Pair Token"
                />
                Price 1{" "}
                {directExchangeDetail.configExchange.destinationToken.name} per{" "}
                {directExchangeDetail.configExchange.originToken.name}
              </div>
              <div className="text-right mt-1 text-number">
                {formatNumberWithMinMaxDigits(
                  parseFloat(destinationPerOrigin.toString()),
                  4,
                  8
                )}
              </div>
            </div>
          </div>

          <ButtonWrapper>
            {!walletApi.isConnected ? (
              <Button
                className="w-full mt-6 mb-6 uppercase md:w-72"
                onClick={unlockWallet}
              >
                CONNECT WALLET
              </Button>
            ) : isInSufficientPoolBalances ? (
              <Button className="w-full mt-6 mb-4 uppercase md:w-72" disabled>
                Out of{" "}
                {directExchangeDetail.configExchange.destinationToken.name}
              </Button>
            ) : isPending ? (
              <Button disabled className="w-full mt-6 mb-4 uppercase md:w-72">
                Processing...
              </Button>
            ) : !isApproved ? (
              <Button
                className="w-full mt-6 mb-4 uppercase md:w-72"
                onClick={approve}
              >
                Approve {directExchangeDetail.configExchange.originToken.name}
              </Button>
            ) : (
              <Button
                className="w-full mt-6 mb-4 uppercase md:w-72"
                onClick={() => setIsConfirmModalOpen(true)}
              >
                Exchange
              </Button>
            )}
          </ButtonWrapper>
          <div className="flex items-center justify-center">
            <div
              className="flex items-center cursor-pointer"
              onClick={addToken}
            >
              <span className="text-lg text-transparent bg-clip-text bg-velo-primary-gradient4 mr-1">
                →
              </span>
              <div className="text-sm">
                Add {directExchangeDetail.configExchange.destinationToken.name}{" "}
                token to wallet
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDirectExchangeModal
        onDirectExchange={onExchange}
        isModalOpen={isConfirmModalOpen}
        setIsModalOpen={setIsConfirmModalOpen}
        directExchangeDetail={directExchangeDetail}
        configExchange={directExchangeDetail.configExchange}
        destinationPerOrigin={destinationPerOrigin}
      />
    </div>
  );
};

export default DirectExchangeUsdtUsdv;
